import React, { useState, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faCamera,
  faCloudUploadAlt,
  faImage,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faQuestionCircle,
  faShieldAlt,
  faInfoCircle,
  faSpinner,
  faTrash,
  faBan,
  faVirus,
  faUserShield,
  faHistory,
  faShareAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import DOMPurify from "dompurify";
import toast from "react-hot-toast";
import LoadingSpinner from "../../common/LoadingSpinner";
import RefreshButton from "../../common/RefreshButton";
import ImageUploadAPI from "../../services/ImageUploadAPI";
import ImageAnalysisAPI from "../../services/ImageAnalysisAPI";
import pollForResult from "../../../../utils/pollForResult";
import SuggestionsBulletList from "../common/SuggestionsBulletList";
import InlineShareForm from "../common/InlineShareForm";
import useShareAlert from "../common/useShareAlert";
import { useSubscription } from "../../context/SubscriptionContext";
import { useLoading } from "../../../../contexts/LoadingContext";
import { useUser } from "../../context/UserContext";
import {
  formatDateTime,
  formatDateOnly,
  formatTimeOnly,
} from "../../utils/DateTimeUtil";
import EmptyStatePanel from "../common/EmptyStatePanel";
import HelpGuideButton from "../../HelpGuideButton";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/ImageAnalysisMobile.css";
import "../../css/Common.css";
import "../text/css/SmsAnalysisSimpleMobile.css";

// ─────────────────────────────────────────────
// Accordion Section
// ─────────────────────────────────────────────
const AccordionSection = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badge,
  headerAction,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ssam-accordion ${open ? "ssam-accordion--open" : ""}`}>
      <button
        type="button"
        className="ssam-accordion-header"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className="ssam-accordion-title">
          {icon && (
            <FontAwesomeIcon icon={icon} className="ssam-accordion-icon" />
          )}
          {title}
          {badge && <span className="ssam-accordion-badge">{badge}</span>}
        </span>
        {headerAction && (
          <span
            className="ssam-accordion-action"
            onClick={(e) => e.stopPropagation()}
          >
            {headerAction}
          </span>
        )}
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
      </button>
      {open && <div className="ssam-accordion-body">{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────
// HTML Sanitization
// ─────────────────────────────────────────────
const EXPLANATION_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "ul",
  "ol",
  "li",
  "h3",
  "h4",
  "h5",
];

const sanitizeExplanationHtml = (html) => {
  if (!html) return "";
  const config = { ALLOWED_TAGS: EXPLANATION_ALLOWED_TAGS, ALLOWED_ATTR: [] };
  let cleaned = DOMPurify.sanitize(html, config);

  // Detect double-encoded HTML (e.g. &lt;p&gt; stored in DB from escapeHtml)
  if (cleaned.includes("&lt;") && cleaned.includes("&gt;")) {
    const temp = document.createElement("div");
    temp.innerHTML = cleaned;
    cleaned = DOMPurify.sanitize(
      temp.textContent || temp.innerText || "",
      config,
    );
  }

  return cleaned;
};

// ─────────────────────────────────────────────
// Main Mobile Component
// ─────────────────────────────────────────────
const ImageAnalysisMobile = () => {
  const { t } = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const { loggedInUser } = useUser();
  const userUuid = loggedInUser?.uuid || loggedInUser?.id;

  // Upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError] = useState(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [context, setContext] = useState("");
  const fileInputRef = useRef(null);

  // Analysis history state
  const [analyses, setAnalyses] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);

  // Slide state
  const [activeSlide, setActiveSlide] = useState("form");
  const share = useShareAlert();
  const { canShare } = useSubscription();
  const isIndividualPlan = !canShare();
  const containerRef = useRef(null);

  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/tiff",
    "image/svg+xml",
  ];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  const scrollTop = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const showDetails = useCallback(() => {
    setActiveSlide("details");
    scrollTop();
  }, [scrollTop]);

  const showForm = useCallback(() => {
    setActiveSlide("form");
    scrollTop();
  }, [scrollTop]);

  // Load analyses on mount
  useEffect(() => {
    loadAnalyses();
    checkQuotaStatus();
  }, [userUuid]);

  const checkQuotaStatus = () => {
    if (!userUuid) return;
    const quotaKey = `image_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
    const exceeded = localStorage.getItem(quotaKey) === "true";
    setQuotaExceeded(exceeded);
  };

  const MAX_ANALYSES = 4;

  const loadAnalyses = async () => {
    try {
      setHistoryLoading(true);
      const data = await ImageAnalysisAPI.searchAnalyses({});

      // Enforce 4-item history limit: delete oldest excess analyses
      if (data && data.length > MAX_ANALYSES) {
        const sorted = [...data].sort(
          (a, b) => new Date(b.analyzedAt) - new Date(a.analyzedAt),
        );
        const excess = sorted.slice(MAX_ANALYSES);
        for (const item of excess) {
          try {
            await ImageAnalysisAPI.deleteAnalysis(item.uploadId);
          } catch (e) {
            console.error("Error deleting excess analysis:", e);
          }
        }
        setAnalyses(sorted.slice(0, MAX_ANALYSES));
      } else {
        setAnalyses(data);
      }
    } catch (err) {
      console.error("Error loading analyses:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      await loadAnalyses();
      toast.success(t("imgMobile.history.refreshSuccess"));
    } catch (err) {
      toast.error(t("imgMobile.history.refreshError") + err.message);
    }
  };

  const handleFileSelect = (file) => {
    setError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t("imgMobile.upload.invalidTypeError"));
      return;
    }

    if (file.size > MAX_SIZE) {
      setError(t("imgMobile.upload.fileTooLargeError"));
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t("imgMobile.upload.noFileError"));
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStatus(t("imgMobile.upload.startingUpload"));

    showLoading(
      t("imgMobile.upload.loadingTitle"),
      t("imgMobile.upload.loadingSubtitle"),
    );

    try {
      const uploadResult = await ImageUploadAPI.uploadImage(
        selectedFile,
        (progress, status) => {
          setUploadProgress(progress);
          setUploadStatus(status);
        },
        context,
      );

      setUploadStatus(t("imgMobile.upload.complete"));

      // Poll for analysis completion using the shared polling utility
      const uploadId = uploadResult?.uploadId;

      pollForResult(
        async () => {
          const exists = uploadId
            ? await ImageAnalysisAPI.analysisExists(uploadId)
            : false;
          return { done: exists };
        },
        {
          interval: 3000,
          maxPolls: 40,
          timeoutMessage: t("imgMobile.upload.timeoutMsg"),
        },
      )
        .then(async () => {
          await loadAnalyses();
          hideLoading();
          toast.success(t("imgMobile.upload.successToast"));

          // Reset upload form
          setSelectedFile(null);
          setPreview(null);
          setUploadProgress(0);
          setUploadStatus("");
          setContext("");
          if (fileInputRef.current) fileInputRef.current.value = "";
        })
        .catch(async () => {
          await loadAnalyses();
          hideLoading();
          toast(t("imgMobile.upload.timeoutMsg"), {
            icon: "ℹ️",
          });
        });
    } catch (err) {
      hideLoading();

      if (err.response?.status === 429) {
        const errorMessage = err.message || err.response?.data?.error || "";
        if (
          errorMessage.includes("Daily image upload limit exceeded") ||
          errorMessage.includes("quota")
        ) {
          const quotaKey = `image_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
          localStorage.setItem(quotaKey, "true");
          setQuotaExceeded(true);
          setError(t("imgMobile.upload.dailyLimitError"));
          setUploadStatus("");
          return;
        }
      }

      setError(err.message || t("imgMobile.upload.failedError"));
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setUploadProgress(0);
    setUploadStatus("");
    setContext("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleViewDetails = (analysis) => {
    setSelectedAnalysis(analysis);
    showDetails();
  };

  const handleDeleteAnalysis = async (uploadId) => {
    try {
      await ImageAnalysisAPI.deleteAnalysis(uploadId);
      await loadAnalyses();

      if (selectedAnalysis && selectedAnalysis.uploadId === uploadId) {
        setSelectedAnalysis(null);
        showForm();
      }
    } catch (err) {
      toast.error(
        t("imgMobile.history.deleteError") +
          (err.message || t("imgMobile.errors.unknown")),
      );
    }
  };

  // ─────────── Helpers ───────────
  const formatScamType = (scamType) => {
    if (!scamType) return t("imgMobile.detail.unknownType");
    return scamType
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getConfidenceLevel = (confidence) => {
    if (confidence >= 0.9) return { label: "Very High", cls: "very-high" };
    if (confidence >= 0.75) return { label: "High", cls: "high" };
    if (confidence >= 0.5) return { label: "Medium", cls: "medium" };
    if (confidence >= 0.25) return { label: "Low", cls: "low" };
    return { label: "Very Low", cls: "very-low" };
  };

  const getScamTypeIcon = (scamType) => {
    const icons = {
      PHISHING: "🎣",
      QR_CODE_SCAM: "📱",
      FAKE_INVOICE: "🧾",
      IMPERSONATION: "🎭",
      LOTTERY_SCAM: "🎰",
      ROMANCE_SCAM: "💔",
      INVESTMENT_SCAM: "💰",
      FINANCIAL_FRAUD: "💳",
      TECH_SUPPORT_SCAM: "💻",
      MALWARE: "🦠",
      SOCIAL_ENGINEERING: "🧠",
      UNKNOWN: "❓",
    };
    return icons[scamType] || "⚠️";
  };

  const extractRecommendations = (explanation, suggestions) => {
    if (!explanation) return suggestions || null;

    const patterns = [
      /###\s*(?:Safety\s+)?Recommendations?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /###\s*Suggestions?\s*\n([\s\S]*?)(?=\n###|\n\n###|$)/i,
      /\*\*(?:Safety\s+)?Recommendations?\*\*\s*\n([\s\S]*?)(?=\n\*\*|\n\n\*\*|$)/i,
    ];

    for (const pattern of patterns) {
      const match = explanation.match(pattern);
      if (match && match[1]) {
        return match[1].trim().replace(/\n{3,}/g, "\n\n");
      }
    }

    return suggestions || null;
  };

  // ─────────── Render Analysis Card ───────────
  const renderAnalysisCard = (analysis) => {
    const analysisResult = analysis.analysisResult || {};
    const confidenceValue = analysisResult.confidence || 0;
    const isScam = analysisResult.isScam === true;

    const securityVerdict =
      analysis.securityVerdict || analysisResult.securityVerdict;
    const isSecurityFailure = securityVerdict === "FAIL";
    const hasMalware =
      analysis.hasMalware || analysisResult.hasMalware || false;

    let statusLabel, statusClass, statusIcon;
    if (isSecurityFailure) {
      statusLabel = hasMalware
        ? t("imgMobile.card.statusVirus")
        : t("imgMobile.card.statusBlocked");
      statusClass = hasMalware ? "ssam-status-danger" : "ssam-status-blocked";
      statusIcon = hasMalware ? faVirus : faBan;
    } else if (isScam) {
      statusLabel = t("imgMobile.card.statusScam");
      statusClass = "ssam-status-danger";
      statusIcon = faExclamationTriangle;
    } else {
      statusLabel = t("imgMobile.card.statusSafe");
      statusClass = "ssam-status-safe";
      statusIcon = faCheckCircle;
    }

    return (
      <div
        key={analysis.uploadId}
        className="ssam-history-card"
        onClick={() => handleViewDetails(analysis)}
      >
        <div className="ssam-card-thumb">
          {isSecurityFailure ? (
            <div className="ssam-thumb-blocked">
              <FontAwesomeIcon icon={statusIcon} />
            </div>
          ) : analysis.imageUrl ? (
            <img src={analysis.imageUrl} alt="Analysis" />
          ) : (
            <div className="ssam-thumb-placeholder">
              <FontAwesomeIcon icon={faImage} />
            </div>
          )}
        </div>
        <div className="ssam-card-info">
          <span className={`ssam-status-chip ${statusClass}`}>
            <FontAwesomeIcon icon={statusIcon} /> {statusLabel}
          </span>
          <span className="ssam-card-date">
            {formatDateOnly(analysis.analyzedAt, "short")}
          </span>
        </div>
        <button
          className="ssam-card-delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteAnalysis(analysis.uploadId);
          }}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>
    );
  };

  // ─────────── Render Detail View ───────────
  const renderDetailView = () => {
    if (!selectedAnalysis) {
      return (
        <div className="ssam-empty-state">
          <EmptyStatePanel
            icon={faImage}
            title={t("imgMobile.detail.noAnalysisTitle")}
            description={t("imgMobile.detail.noAnalysisDesc")}
          />
          <button
            type="button"
            className="ssam-btn ssam-btn-primary"
            onClick={showForm}
          >
            {t("imgMobile.detail.backToUpload")}
          </button>
        </div>
      );
    }

    const analysisResult = selectedAnalysis.analysisResult || {};
    const confidence = analysisResult.confidence || 0;
    const scamType = analysisResult.scamType;
    const explanation = analysisResult.explanation;
    const riskFactors = analysisResult.riskFactors || [];
    const suggestions = analysisResult.suggestions;
    const isScam = analysisResult.isScam === true;

    const securityVerdict =
      selectedAnalysis.securityVerdict || analysisResult.securityVerdict;
    const isSecurityFailure = securityVerdict === "FAIL";
    const hasMalware =
      selectedAnalysis.hasMalware || analysisResult.hasMalware || false;
    const processingError =
      selectedAnalysis.processingError || analysisResult.processingError;

    const confidenceLevel = getConfidenceLevel(confidence);
    const recommendations = extractRecommendations(explanation, suggestions);

    return (
      <>
        {/* Threat Banner (desktop style, flattened) */}
        <div className="case-details">
          <div
            className={`threat-banner sasm-threat-banner ${
              isSecurityFailure
                ? "security-failed"
                : isScam
                  ? "threat-detected"
                  : "safe"
            }`}
          >
            <div className="threat-icon">
              <FontAwesomeIcon
                icon={
                  isSecurityFailure
                    ? hasMalware
                      ? faVirus
                      : faBan
                    : isScam
                      ? faExclamationTriangle
                      : faCheckCircle
                }
              />
            </div>
            <h2 className="threat-title">
              {isSecurityFailure
                ? hasMalware
                  ? t("imgMobile.detail.malwareDetected")
                  : t("imgMobile.detail.securityFailed")
                : isScam
                  ? t("imgMobile.detail.scamDetected")
                  : t("imgMobile.detail.noThreats")}
            </h2>
            {(isScam || (isSecurityFailure && hasMalware)) && (
              <button
                className="threat-banner-share-btn"
                onClick={
                  isIndividualPlan
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        share.toggleShare();
                      }
                }
                disabled={isIndividualPlan}
                title={
                  isIndividualPlan
                    ? t("imgMobile.detail.shareIndividual")
                    : t("imgMobile.detail.shareFamily")
                }
              >
                <FontAwesomeIcon
                  icon={faShareAlt}
                  className="threat-share-icon"
                />
                <span className="threat-share-label">
                  {t("imgMobile.detail.share")}
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Inline Share Form */}
        {share.shareOpen && (isScam || (isSecurityFailure && hasMalware)) && (
          <InlineShareForm
            shareSuccess={share.shareSuccess}
            shareError={share.shareError}
            shareMessage={share.shareMessage}
            setShareMessage={share.setShareMessage}
            shareLoading={share.shareLoading}
            onCancel={share.closeShare}
            onSend={() => share.sendShare(null)}
          />
        )}

        {/* Security Failure Notice */}
        {isSecurityFailure && (
          <div className="ssam-security-notice">
            <FontAwesomeIcon
              icon={faUserShield}
              className="ssam-security-notice-icon"
            />
            <div>
              <strong>{t("imgMobile.detail.blockedTitle")}</strong>
              <p>
                {hasMalware
                  ? t("imgMobile.detail.malwareNotice")
                  : t("imgMobile.detail.securityNotice")}
              </p>
              {processingError && (
                <p className="ssam-error-detail">
                  <strong>{t("imgMobile.detail.blockedReason")}</strong>{" "}
                  {processingError}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Scam Type (skip if Unknown) */}
        {isScam && scamType && scamType !== "UNKNOWN" && (
          <div className="ssam-scam-type-banner">
            <span className="ssam-scam-type-emoji">
              {getScamTypeIcon(scamType)}
            </span>
            <span className="ssam-scam-type-label">
              {t("imgMobile.detail.scamTypeLabel")}{" "}
              <strong>{formatScamType(scamType)}</strong>
            </span>
          </div>
        )}

        {/* What Should You Do? (desktop style) */}
        {recommendations && (
          <div className="suggestions-section">
            <h4 className="subsection-title">
              <FontAwesomeIcon
                icon={faInfoCircle}
                className="fa-icon-margin-right-8"
              />
              {t("imgMobile.detail.whatToDo")}
            </h4>
            <SuggestionsBulletList text={recommendations} />
          </div>
        )}

        {/* Explanation (desktop style, collapsible) */}
        {explanation && (
          <AccordionSection
            title={t("imgMobile.detail.explanationTitle")}
            icon={faSearch}
            defaultOpen={false}
          >
            <div
              className="ssam-explanation-text"
              dangerouslySetInnerHTML={{
                __html: sanitizeExplanationHtml(explanation),
              }}
            />
          </AccordionSection>
        )}

        {/* Image Preview */}
        {!isSecurityFailure && selectedAnalysis.imageUrl && (
          <AccordionSection
            title={t("imgMobile.detail.imagePreviewTitle")}
            icon={faImage}
            defaultOpen={false}
          >
            <div className="ssam-image-preview">
              <img
                src={selectedAnalysis.imageUrl}
                alt={selectedAnalysis.fileName}
              />
            </div>
          </AccordionSection>
        )}

        {/* Metadata */}
        {selectedAnalysis.analyzedAt && (
          <div className="ssam-metadata">
            {t("imgMobile.detail.analyzedOn").replace(
              "{date}",
              formatDateTime(selectedAnalysis.analyzedAt),
            )}
          </div>
        )}

        {/* Back / Delete */}
        <div className="ssam-detail-actions">
          <button
            type="button"
            className="ssam-btn ssam-btn-primary"
            onClick={showForm}
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} />
            {t("imgMobile.detail.uploadAnother")}
          </button>
          <button
            type="button"
            className="ssam-btn ssam-btn-danger"
            onClick={() => handleDeleteAnalysis(selectedAnalysis.uploadId)}
          >
            <FontAwesomeIcon icon={faTrash} />
            {t("imgMobile.detail.deleteBtn")}
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="ssam-container" ref={containerRef}>
      <div
        className={`ssam-view-slider ${
          activeSlide === "details" ? "ssam-slide-details" : ""
        }`}
      >
        {/* ── SLIDE 1: Upload + History ── */}
        <div className="ssam-slide ssam-slide-form">
          <div className="ssam-form-wrapper">
            {/* Page Header */}
            <div className="sasm-header">
              <div className="sasm-header-top">
                <h1 className="main-title">{t("imgMobile.page.title")}</h1>
                <HelpGuideButton guideId={6} />
              </div>
              <p className="common-subtitle">{t("imgMobile.page.subtitle")}</p>
            </div>

            {/* Quota Banner */}
            {quotaExceeded && (
              <div className="ssam-quota-banner">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <strong>{t("imgMobile.quota.title")}</strong>
                  <p>{t("imgMobile.quota.desc")}</p>
                </div>
              </div>
            )}

            {/* Error Banner */}
            {error && (
              <div className="ssam-error-banner">
                <FontAwesomeIcon icon={faTimesCircle} />
                <span>{error}</span>
              </div>
            )}

            {/* Upload Area */}
            {selectedFile && preview ? (
              <div className={`ssam-upload-zone ssam-upload-has-file`}>
                <div className="ssam-upload-preview">
                  <img src={preview} alt="Preview" />
                  <span className="ssam-file-name">{selectedFile.name}</span>
                </div>
              </div>
            ) : (
              <div className="ssam-upload-methods">
                <div
                  className="ssam-upload-zone ssam-browse-zone"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="ssam-upload-prompt">
                    <FontAwesomeIcon
                      icon={faCamera}
                      className="ssam-upload-icon"
                    />
                    <strong>{t("imgMobile.upload.prompt")}</strong>
                    <span className="ssam-upload-formats">
                      {t("imgMobile.upload.formats")}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_TYPES.join(",")}
              onChange={handleFileInputChange}
              style={{ display: "none" }}
              disabled={uploading}
            />

            {/* Upload Progress */}
            {uploading && (
              <div className="ssam-progress-section">
                <div className="ssam-progress-header">
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>{uploadStatus}</span>
                </div>
                <div className="ssam-progress-bar">
                  <div
                    className="ssam-progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <span className="ssam-progress-pct">
                  {uploadProgress.toFixed(0)}%
                </span>
              </div>
            )}

            {/* Context Field */}
            {selectedFile && !uploading && (
              <div className="form-group" style={{ marginTop: "1rem" }}>
                <label htmlFor="image-context-input-mobile">
                  <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
                  {t("imgMobile.upload.contextLabel")}
                </label>
                <textarea
                  id="image-context-input-mobile"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  placeholder={t("imgMobile.upload.contextPlaceholder")}
                  className="additional-context-textarea"
                  rows={3}
                />
              </div>
            )}

            {/* Action Buttons */}
            {selectedFile && !uploading && (
              <div className="ssam-form-actions">
                <button
                  type="button"
                  className="ssam-btn ssam-btn-primary"
                  onClick={handleUpload}
                  disabled={quotaExceeded}
                >
                  <FontAwesomeIcon icon={faCloudUploadAlt} />
                  {t("imgMobile.upload.analyzeBtn")}
                </button>
                <button
                  type="button"
                  className="ssam-btn ssam-btn-secondary"
                  onClick={handleCancel}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                  {t("imgMobile.upload.cancelBtn")}
                </button>
              </div>
            )}

            {/* Analysis History */}
            <AccordionSection
              title={t("imgMobile.history.title")}
              icon={faHistory}
              defaultOpen={true}
              badge={analyses.length > 0 ? analyses.length : undefined}
              headerAction={
                <RefreshButton
                  onClick={handleRefresh}
                  variant="ghost"
                  loading={historyLoading}
                />
              }
            >
              {historyLoading ? (
                <div className="ssam-history-loading">
                  <LoadingSpinner
                    size="sm"
                    message={t("imgMobile.history.loading")}
                    className="compact"
                  />
                </div>
              ) : analyses.length === 0 ? (
                <div className="ssam-history-empty">
                  <FontAwesomeIcon icon={faImage} className="ssam-empty-icon" />
                  <p>{t("imgMobile.history.empty")}</p>
                </div>
              ) : (
                <div className="ssam-history-list">
                  {analyses
                    .sort(
                      (a, b) => new Date(b.analyzedAt) - new Date(a.analyzedAt),
                    )
                    .slice(0, 10)
                    .map(renderAnalysisCard)}
                  {analyses.length > 10 && (
                    <div className="ssam-history-more">
                      {t("imgMobile.history.showing").replace(
                        "{total}",
                        analyses.length,
                      )}
                    </div>
                  )}
                </div>
              )}
            </AccordionSection>
          </div>
        </div>

        {/* ── SLIDE 2: Analysis Details ── */}
        <div className="ssam-slide ssam-slide-detail">
          <div className="ssam-results-wrapper">
            {/* Back button */}
            <button type="button" className="ssam-back-btn" onClick={showForm}>
              <FontAwesomeIcon icon={faArrowLeft} />
              {t("imgMobile.detail.backToUpload")}
            </button>

            {renderDetailView()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageAnalysisMobile;
